import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Calendar, Wrench, ArrowRightLeft, ShieldAlert, History, Activity } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { apiGet, apiList } from "@/integrations/api/client";
import { format } from "date-fns";

export const Route = createFileRoute("/_authenticated/reports/asset-history")({
  component: AssetHistoryCard,
});

type AssetRow = {
  id: string;
  asset_code: string;
  name: string;
  serial_number: string | null;
  status: string;
  make: string | null;
  model: string | null;
  location: { name: string } | null;
  department: { name: string } | null;
};

function useAssetSearch(query: string) {
  const q = query.trim();
  return useQuery({
    queryKey: ["asset-history-search", q],
    queryFn: async () => {
      const res = await apiList<AssetRow>("/api/v1/assets/", { search: q || undefined });
      return (res.results || []).slice(0, 20);
    },
    staleTime: 30_000,
  });
}

function AssetPicker({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (id: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 250);
    return () => clearTimeout(t);
  }, [query]);

  const { data: results, isFetching } = useAssetSearch(debounced);
  
  const { data: selected } = useQuery({
    queryKey: ["asset-lookup", value],
    enabled: !!value,
    queryFn: () => apiGet<AssetRow>(`/api/v1/assets/${value}/`),
  });

  const label = selected ? `${selected.asset_code} — ${selected.name}` : "Select an asset to view history...";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full max-w-lg justify-between font-normal text-left", !selected && "text-muted-foreground")}
        >
          <span className="truncate">{label}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search by code, name, or serial…" value={query} onValueChange={setQuery} />
          <CommandList>
            {isFetching && (
              <div className="flex items-center justify-center py-4 text-xs text-muted-foreground">
                <Loader2 className="mr-2 h-3 w-3 animate-spin" /> Searching…
              </div>
            )}
            <CommandEmpty>{isFetching ? null : "No assets found."}</CommandEmpty>
            <CommandGroup>
              {(results ?? []).map((a) => (
                <CommandItem
                  key={a.id}
                  value={a.id}
                  onSelect={(v) => {
                    onChange(v);
                    setOpen(false);
                  }}
                >
                  <Check className={cn("mr-2 h-4 w-4", value === a.id ? "opacity-100" : "opacity-0")} />
                  <div className="flex flex-col">
                    <span>{a.asset_code} — {a.name}</span>
                    <span className="text-xs text-muted-foreground">
                      {a.department?.name || a.location?.name || "No location"} {a.serial_number ? `| SN: ${a.serial_number}` : ""}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function useAssetHistory(assetId: string | null) {
  return useQuery({
    queryKey: ["asset-history", assetId],
    enabled: !!assetId,
    queryFn: async () => {
      if (!assetId) return { events: [], asset: null };
      
      const [asset, wos, pms, transfers, audits] = await Promise.all([
        apiGet<AssetRow>(`/api/v1/assets/${assetId}/`),
        apiList<any>("/api/v1/work-orders/", { asset: assetId, limit: 100 }),
        apiList<any>("/api/v1/pm/schedules/", { asset: assetId, limit: 100 }),
        apiList<any>("/api/v1/transfers/transfers/", { asset: assetId, limit: 100 }),
        apiList<any>("/api/v1/audit-trail/", { record_id: assetId, limit: 100 }),
      ]);

      type TimelineEvent = {
        id: string;
        date: string;
        type: "WO" | "PM" | "TRANSFER" | "AUDIT";
        title: string;
        description: string;
        status?: string;
        link?: string;
      };

      const events: TimelineEvent[] = [];

      (wos.results || []).forEach((wo: any) => {
        events.push({
          id: `wo-${wo.id}`,
          date: wo.reported_at || wo.created_at,
          type: "WO",
          title: `Work Order: ${wo.wo_number || wo.wo_no}`,
          description: wo.problem_description || "No description",
          status: wo.status,
          link: `/work-orders/${wo.id}`
        });
      });

      (pms.results || []).forEach((pm: any) => {
        events.push({
          id: `pm-${pm.id}`,
          date: pm.scheduled_date,
          type: "PM",
          title: `PM Schedule: ${pm.schedule_number || "Maintenance"}`,
          description: pm.maintenance_type || "Preventive Maintenance",
          status: pm.status,
        });
      });

      (transfers.results || []).forEach((tr: any) => {
        events.push({
          id: `tr-${tr.id}`,
          date: tr.created_at,
          type: "TRANSFER",
          title: `Transfer: ${tr.transfer_number}`,
          description: `From ${tr.from_department?.name || "Unknown"} to ${tr.to_department?.name || "Unknown"}`,
          status: tr.status,
          link: `/transfers/${tr.id}`
        });
      });

      (audits.results || []).forEach((aud: any) => {
        if (!aud.changes || Object.keys(aud.changes).length === 0) return;
        events.push({
          id: `aud-${aud.id}`,
          date: aud.timestamp || aud.created_at,
          type: "AUDIT",
          title: `Asset Updated by ${aud.user_name || aud.user || "System"}`,
          description: `Action: ${aud.action}. Fields updated: ${Object.keys(aud.changes).join(", ")}`,
        });
      });

      events.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

      return { asset, events };
    }
  });
}

function AssetHistoryCard() {
  const [assetId, setAssetId] = useState<string | null>(null);
  const { data, isLoading } = useAssetHistory(assetId);

  const getIcon = (type: string) => {
    switch (type) {
      case "WO": return <Wrench className="h-5 w-5 text-orange-500" />;
      case "PM": return <Calendar className="h-5 w-5 text-blue-500" />;
      case "TRANSFER": return <ArrowRightLeft className="h-5 w-5 text-purple-500" />;
      case "AUDIT": return <History className="h-5 w-5 text-slate-500" />;
      default: return <Activity className="h-5 w-5 text-slate-500" />;
    }
  };

  const getBadgeVariant = (status?: string) => {
    if (!status) return "outline";
    const s = status.toLowerCase();
    if (s.includes("closed") || s.includes("completed") || s.includes("resolved")) return "default";
    if (s.includes("open") || s.includes("pending") || s.includes("assigned")) return "destructive";
    if (s.includes("progress")) return "secondary";
    return "outline";
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Asset History Card (R-02)</h1>
          <p className="text-muted-foreground">View the complete lifecycle and event timeline for an asset.</p>
        </div>
        <div className="w-full md:w-auto">
          <AssetPicker value={assetId} onChange={setAssetId} />
        </div>
      </div>

      {isLoading && (
        <Card>
          <CardContent className="p-12 flex justify-center items-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mr-3" />
            <span className="text-muted-foreground">Loading asset history...</span>
          </CardContent>
        </Card>
      )}

      {!isLoading && !assetId && (
        <Card className="bg-muted/30 border-dashed">
          <CardContent className="p-12 flex flex-col items-center justify-center text-center">
            <Activity className="h-12 w-12 text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-medium text-foreground">No Asset Selected</h3>
            <p className="text-muted-foreground mt-2 max-w-md">
              Please search and select an asset from the picker above to view its full lifecycle timeline, including maintenance, transfers, and updates.
            </p>
          </CardContent>
        </Card>
      )}

      {!isLoading && data?.asset && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="md:col-span-1 border-primary/20 bg-primary/5 h-fit">
            <CardHeader>
              <CardTitle className="text-lg">Asset Details</CardTitle>
              <CardDescription>Current snapshot of the asset.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Asset Name & Code</p>
                <p className="font-semibold text-lg">{data.asset.name}</p>
                <p className="font-mono text-sm text-primary">{data.asset.asset_code}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Status</p>
                  <Badge variant={data.asset.status === "Active" ? "default" : "secondary"}>
                    {data.asset.status}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Serial No.</p>
                  <p className="font-medium">{data.asset.serial_number || "—"}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm font-medium text-muted-foreground">Location</p>
                  <p className="font-medium">
                    {[data.asset.department?.name, data.asset.location?.name].filter(Boolean).join(" / ") || "—"}
                  </p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm font-medium text-muted-foreground">Make & Model</p>
                  <p className="font-medium">
                    {[data.asset.make, data.asset.model].filter(Boolean).join(" - ") || "—"}
                  </p>
                </div>
              </div>
              <div className="pt-4 mt-4 border-t border-primary/10">
                <Button variant="outline" className="w-full" asChild>
                  <Link to="/assets/$assetId" params={{ assetId: data.asset.id }}>
                    View Full Profile
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle className="text-lg">Lifecycle Timeline</CardTitle>
              <CardDescription>{data.events.length} events recorded for this asset.</CardDescription>
            </CardHeader>
            <CardContent>
              {data.events.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No historical events found for this asset.
                </div>
              ) : (
                <div className="relative border-l-2 border-muted ml-3 pl-6 space-y-8 pb-4">
                  {data.events.map((ev) => (
                    <div key={ev.id} className="relative">
                      <div className="absolute -left-[35px] top-1 rounded-full bg-background p-1 border-2 border-muted">
                        {getIcon(ev.type)}
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
                        <div>
                          <h4 className="font-semibold flex items-center gap-2">
                            {ev.link ? (
                              <Link to={ev.link} className="hover:underline text-primary">
                                {ev.title}
                              </Link>
                            ) : (
                              ev.title
                            )}
                            {ev.status && (
                              <Badge variant={getBadgeVariant(ev.status)} className="text-[10px] px-1.5 py-0">
                                {ev.status}
                              </Badge>
                            )}
                          </h4>
                          <p className="text-sm text-muted-foreground mt-1">{ev.description}</p>
                        </div>
                        <span className="text-xs font-mono text-muted-foreground whitespace-nowrap bg-muted/30 px-2 py-1 rounded">
                          {format(new Date(ev.date), "dd MMM yyyy, HH:mm")}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
